import { useEffect, useState, useCallback } from "react";
import { api, API } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import { Notepad, DownloadSimple, MagnifyingGlass, ArrowClockwise, Users, ListBullets, CircleNotch } from "@phosphor-icons/react";

const CAT_TONE = {
  auth: "bg-[#0055FF]/10 text-[#0055FF]",
  integration: "bg-[#7C3AED]/10 text-[#7C3AED]",
  email: "bg-[#059669]/10 text-[#059669]",
  sms: "bg-[#D97706]/10 text-[#D97706]",
  system: "bg-neutral-200 text-neutral-700",
};
const tone = (c) => CAT_TONE[c] || "bg-neutral-100 text-neutral-600";
const statusTone = (s) => (s === "fail" || s === "error" ? "text-[#DC2626]" : "text-[#059669]");
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");
const dur = (s) => {
  if (s == null) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

function EventsTab() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ categories: [], actions: [] });
  const [filters, setFilters] = useState({ category: "", action: "", q: "", start: "", end: "", status: "" });
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const load = useCallback(() => {
    setLoading(true);
    const params = { ...filters, page, page_size: pageSize };
    api.get("/admin/audit-logs", { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/audit-logs/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);

  const setF = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const exportUrl = () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    window.open(`${API}/admin/audit-logs/export?${qs}`, "_blank");
  };
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  return (
    <div data-testid="logs-events-tab">
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <div className="relative col-span-2 sm:col-span-3 lg:col-span-2">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="logs-search"
            value={filters.q}
            onChange={(e) => setF("q", e.target.value)}
            placeholder="Search user, action, IP…"
            className="w-full rounded-sm border border-neutral-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#0055FF]"
          />
        </div>
        <select data-testid="logs-filter-category" value={filters.category} onChange={(e) => setF("category", e.target.value)} className="rounded-sm border border-neutral-300 px-2.5 py-2 text-sm outline-none focus:border-[#0055FF]">
          <option value="">All categories</option>
          {facets.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="logs-filter-action" value={filters.action} onChange={(e) => setF("action", e.target.value)} className="rounded-sm border border-neutral-300 px-2.5 py-2 text-sm outline-none focus:border-[#0055FF]">
          <option value="">All actions</option>
          {facets.actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input type="date" data-testid="logs-filter-start" value={filters.start} onChange={(e) => setF("start", e.target.value)} className="rounded-sm border border-neutral-300 px-2.5 py-2 text-sm outline-none focus:border-[#0055FF]" />
        <input type="date" data-testid="logs-filter-end" value={filters.end} onChange={(e) => setF("end", e.target.value)} className="rounded-sm border border-neutral-300 px-2.5 py-2 text-sm outline-none focus:border-[#0055FF]" />
      </div>

      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-neutral-500" data-testid="logs-total">{data.total.toLocaleString()} events</p>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="logs-refresh" className="flex items-center gap-1.5 rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"><ArrowClockwise size={15} weight="bold" /> Refresh</button>
          <button onClick={exportUrl} data-testid="logs-export" className="flex items-center gap-1.5 rounded-sm bg-[#0A0A0A] px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-[#0055FF]"><DownloadSimple size={15} weight="bold" /> Export CSV</button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-sm border border-neutral-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 font-semibold">Time</th>
              <th className="px-4 py-3 font-semibold">Category</th>
              <th className="px-4 py-3 font-semibold">Action</th>
              <th className="px-4 py-3 font-semibold">User</th>
              <th className="px-4 py-3 font-semibold">IP</th>
              <th className="px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading ? (
              <tr><td colSpan={6} className="py-12 text-center"><CircleNotch size={22} className="mx-auto animate-spin text-[#0055FF]" /></td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={6} className="py-12 text-center text-neutral-400" data-testid="logs-empty">No log entries match these filters.</td></tr>
            ) : data.items.map((r) => (
              <tr key={r.id} className="hover:bg-neutral-50" data-testid="logs-row">
                <td className="whitespace-nowrap px-4 py-2.5 text-neutral-600">{fmt(r.ts)}</td>
                <td className="px-4 py-2.5"><span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${tone(r.category)}`}>{r.category}</span></td>
                <td className="px-4 py-2.5 font-medium text-[#0A0A0A]">{r.action}{r.detail && Object.keys(r.detail).length > 0 && <span className="ml-2 text-xs text-neutral-400">{JSON.stringify(r.detail).slice(0, 60)}</span>}</td>
                <td className="px-4 py-2.5 text-neutral-600">{r.user_email || "—"}</td>
                <td className="px-4 py-2.5 text-neutral-500">{r.ip || "—"}</td>
                <td className={`px-4 py-2.5 font-semibold ${statusTone(r.status)}`}>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="logs-prev" className="rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40">Prev</button>
          <span className="text-sm text-neutral-500">Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="logs-next" className="rounded-sm border border-neutral-300 px-3 py-1.5 text-sm font-semibold disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

function SessionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeOnly, setActiveOnly] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/sessions", { params: { active_only: activeOnly, limit: 200 } })
      .then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  }, [activeOnly]);

  return (
    <div data-testid="logs-sessions-tab">
      <label className="mb-4 flex w-fit items-center gap-2 text-sm text-neutral-600">
        <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} data-testid="sessions-active-only" />
        Active sessions only
      </label>
      <div className="overflow-x-auto rounded-sm border border-neutral-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 font-semibold">User</th>
              <th className="px-4 py-3 font-semibold">Logged in</th>
              <th className="px-4 py-3 font-semibold">Last seen</th>
              <th className="px-4 py-3 font-semibold">Duration</th>
              <th className="px-4 py-3 font-semibold">IP</th>
              <th className="px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading ? (
              <tr><td colSpan={6} className="py-12 text-center"><CircleNotch size={22} className="mx-auto animate-spin text-[#0055FF]" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="py-12 text-center text-neutral-400" data-testid="sessions-empty">No sessions recorded yet.</td></tr>
            ) : items.map((s) => (
              <tr key={s.session_id} className="hover:bg-neutral-50" data-testid="sessions-row">
                <td className="px-4 py-2.5 font-medium text-[#0A0A0A]">{s.user_email}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-neutral-600">{fmt(s.login_at)}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-neutral-600">{fmt(s.last_seen)}</td>
                <td className="px-4 py-2.5 font-semibold text-[#0A0A0A]">{dur(s.duration_sec)}</td>
                <td className="px-4 py-2.5 text-neutral-500">{s.ip || "—"}</td>
                <td className="px-4 py-2.5">
                  {s.active
                    ? <span className="rounded-sm bg-[#059669]/10 px-2 py-0.5 text-xs font-semibold text-[#059669]">Active</span>
                    : <span className="rounded-sm bg-neutral-200 px-2 py-0.5 text-xs font-semibold text-neutral-600">Ended</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Logs() {
  const [tab, setTab] = useState("events");
  return (
    <Layout wide>
      <div data-testid="logs-page">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0A0A0A] text-white"><Notepad size={22} weight="fill" /></div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]">Activity &amp; System Logs</h1>
            <p className="text-sm text-neutral-500">User logins, sessions, integration activity and system events.</p>
          </div>
        </div>

        <div className="mb-5 flex gap-2 border-b border-neutral-200">
          <button onClick={() => setTab("events")} data-testid="logs-tab-events" className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === "events" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"}`}>
            <ListBullets size={16} weight="bold" /> Events
          </button>
          <button onClick={() => setTab("sessions")} data-testid="logs-tab-sessions" className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${tab === "sessions" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"}`}>
            <Users size={16} weight="bold" /> Sessions
          </button>
        </div>

        {tab === "events" ? <EventsTab /> : <SessionsTab />}

        <PageAdvice items={[
          "Every login, logout, password re-verification and data-sync trigger is recorded here automatically.",
          "Session duration is measured from login until the last activity heartbeat (or logout).",
          "Email and SMS sends will appear here once those channels are connected.",
        ]} />
      </div>
    </Layout>
  );
}
